import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  InputLabel,
  Checkbox,
  FormControlLabel,
  TextField,
  Tabs,
  Tab,
  Box
} from "@mui/material";

const defaultOpeningTimes = {
  Monday: { open: "09:00", close: "18:00" },
  Tuesday: { open: "09:00", close: "18:00" },
  Wednesday: { open: "09:00", close: "18:00" },
  Thursday: { open: "09:00", close: "18:00" },
  Friday: { open: "09:00", close: "18:00" },
  Saturday: { open: "09:00", close: "13:00" },
  Sunday: { open: "00:00", close: "00:00" },
};

// Default patient app settings
const defaultPatientAppSettings = {
  allow_messaging: true,
  allow_replies: false,
  allow_booking: false,
  repeats: false,
  medication_reminders: false,
  show_phone_number: true,
  primary_hex: '#2D5A3D',
  shop_enabled: false,
  shop_id: '',
  pharmacy_phone_number: '',
  pharmacy_phone_lines: [], // Array of { label, number }
  buttons: [], // Array of { label, url }
  top_box_text: ''
};

const PatientAppSection = ({ settings, onUpdate, onInherit }) => {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Patient App Settings</h4>
        <Button
          variant="outlined"
          size="small"
          onClick={onInherit}
        >
          Inherit from organisation
        </Button>
      </div>

      {/* Feature toggles */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Features</h5>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allow_messaging || false}
                onChange={(e) => onUpdate('allow_messaging', e.target.checked)}
              />
            }
            label="Enable message view"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allow_replies || false}
                onChange={(e) => onUpdate('allow_replies', e.target.checked)}
              />
            }
            label="Allow message replies"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.allow_booking || false}
                onChange={(e) => onUpdate('allow_booking', e.target.checked)}
              />
            }
            label="Enable clinic booking"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.repeats || false}
                onChange={(e) => onUpdate('repeats', e.target.checked)}
              />
            }
            label="Allow repeat ordering"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.medication_reminders || false}
                onChange={(e) => onUpdate('medication_reminders', e.target.checked)}
              />
            }
            label="Show medication reminders"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.show_phone_number !== false}
                onChange={(e) => onUpdate('show_phone_number', e.target.checked)}
              />
            }
            label="Show pharmacy phone number"
          />
        </div>
      </div>

      {/* Theme */}
      <div style={{ marginBottom: 12 }}>
        <InputLabel>Theme (Primary Color)</InputLabel>
        <input
          type="color"
          value={settings.primary_hex || '#2D5A3D'}
          onChange={(e) => onUpdate('primary_hex', e.target.value)}
          style={{ width: 60, height: 40, borderRadius: 4, cursor: "pointer" }}
        />
      </div>

      {/* Contact Information */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Contact Information</h5>
        
        <InputLabel>Main pharmacy phone number</InputLabel>
        <Input
          type="tel"
          value={settings.pharmacy_phone_number || ''}
          onChange={(e) => onUpdate('pharmacy_phone_number', e.target.value)}
          placeholder="07540876708"
          fullWidth
          style={{ marginBottom: 12 }}
        />

        <h6 style={{ marginBottom: 8 }}>Phone lines (up to 3)</h6>
        {[0, 1, 2].map((idx) => {
          const line = (settings.pharmacy_phone_lines || [])[idx] || { label: '', number: '' };
          return (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <TextField
                label="Label"
                size="small"
                value={line.label || ''}
                onChange={(e) => {
                  const updated = [...(settings.pharmacy_phone_lines || [])];
                  updated[idx] = { ...line, label: e.target.value };
                  onUpdate('pharmacy_phone_lines', updated.filter(l => l.label || l.number));
                }}
                placeholder="e.g., Repeats"
                style={{ flex: 0.4 }}
              />
              <TextField
                label="Number"
                size="small"
                value={line.number || ''}
                onChange={(e) => {
                  const updated = [...(settings.pharmacy_phone_lines || [])];
                  updated[idx] = { ...line, number: e.target.value };
                  onUpdate('pharmacy_phone_lines', updated.filter(l => l.label || l.number));
                }}
                placeholder="Phone number"
                style={{ flex: 0.6 }}
              />
            </div>
          );
        })}
      </div>

      {/* Shortcut Buttons */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Shortcut Buttons (up to 4)</h5>
        {[0, 1, 2, 3].map((idx) => {
          const button = (settings.buttons || [])[idx] || { label: '', url: '' };
          return (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <TextField
                label="Label"
                size="small"
                value={button.label || ''}
                onChange={(e) => {
                  const updated = [...(settings.buttons || [])];
                  updated[idx] = { ...button, label: e.target.value };
                  onUpdate('buttons', updated.filter(b => b.label || b.url));
                }}
                placeholder="e.g., NHS 111"
                style={{ flex: 0.3 }}
              />
              <TextField
                label="URL"
                size="small"
                value={button.url || ''}
                onChange={(e) => {
                  const updated = [...(settings.buttons || [])];
                  updated[idx] = { ...button, url: e.target.value };
                  onUpdate('buttons', updated.filter(b => b.label || b.url));
                }}
                placeholder="https://..."
                style={{ flex: 0.7 }}
              />
            </div>
          );
        })}
      </div>

      {/* Online Shop */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12, 
        marginBottom: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Online Shop</h5>
        <FormControlLabel
          control={
            <Checkbox
              checked={settings.shop_enabled || false}
              onChange={(e) => onUpdate('shop_enabled', e.target.checked)}
            />
          }
          label="Show online shop"
        />
        {settings.shop_enabled && (
          <>
            <InputLabel sx={{ mt: 2 }}>Pharmacy EPOS shopId</InputLabel>
            <Input
              value={settings.shop_id || ''}
              onChange={(e) => onUpdate('shop_id', e.target.value)}
              placeholder="UUID"
              fullWidth
            />
          </>
        )}
      </div>

      {/* Pharmacy News */}
      <div style={{ 
        border: "1px solid #e5e7eb", 
        borderRadius: 6, 
        padding: 12,
        backgroundColor: "#f9fafb"
      }}>
        <h5 style={{ marginTop: 0, marginBottom: 12 }}>Pharmacy News</h5>
        <InputLabel>Top box text (news/updates)</InputLabel>
        <TextField
          multiline
          rows={3}
          value={settings.top_box_text || ''}
          onChange={(e) => onUpdate('top_box_text', e.target.value)}
          placeholder="Important news displayed at top of app..."
          fullWidth
          style={{ marginTop: 8 }}
        />
      </div>
    </div>
  );
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const Settings = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [groupData, setGroupData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [checkboxes, setCheckboxes] = useState({
    scanToShelf: true,
    inventory: true,
    housekeeping: true,
    activities: true,
    communications: true,
    report_sim: false,
    report_expiring: false,
    report_slow: false,
    report_fridge: false,
    send_timesheets: false,
  });

  const [timesheetDay, setTimesheetDay] = useState(1);

  const [openingTimes, setOpeningTimes] = useState(defaultOpeningTimes);
  const [selectedPharmacyIndex, setSelectedPharmacyIndex] = useState(0);
  const [managerEmail, setManagerEmail] = useState("");
  const [ocsCode, setOcsCode] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [patientAppSettings, setPatientAppSettings] = useState({ ...defaultPatientAppSettings });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPharmacyName, setNewPharmacyName] = useState("");
  const [newPharmacyCode, setNewPharmacyCode] = useState("");
  const [addError, setAddError] = useState("");

  useEffect(() => {
    if (open) {
      const savedEmail = localStorage.getItem("manager_email") || "";
      setManagerEmail(savedEmail);

      const organisation_name = localStorage.getItem("organisationName") || "";
      const expireBefore = parseInt(localStorage.getItem("expireBefore")) || 0;
      const slowBefore = parseInt(localStorage.getItem("slowBefore")) || 0;
      const members = JSON.parse(localStorage.getItem("members")) || [];

      const parsed = {
        organisation_name,
        expireBefore,
        slowBefore,
        members,
        email: localStorage.getItem("email") || ""
      };

      setGroupData(parsed);
      setEditedData(JSON.parse(JSON.stringify(parsed)));

      const storedCheckboxes = {
        scanToShelf: JSON.parse(localStorage.getItem("scanToShelf") || "true"),
        inventory: JSON.parse(localStorage.getItem("inventory") || "true"),
        housekeeping: JSON.parse(localStorage.getItem("housekeeping") || "true"),
        activities: JSON.parse(localStorage.getItem("activities") || "true"),
        communications: JSON.parse(localStorage.getItem("communications") || "true"),
        report_sim: JSON.parse(localStorage.getItem("report_sim") || "false"),
        report_expiring: JSON.parse(localStorage.getItem("report_expiring") || "false"),
        report_slow: JSON.parse(localStorage.getItem("report_slow") || "false"),
        report_fridge: JSON.parse(localStorage.getItem("report_fridge") || "false"),
        send_timesheets: JSON.parse(localStorage.getItem("send_timesheets") || "false"),
      };

      setCheckboxes(storedCheckboxes);

      const storedDay = parseInt(localStorage.getItem("timesheet_day")) || 1;
      setTimesheetDay(storedDay);

      if (members.length > 0) {
        const first = members[0];
        const firstTimes = first.openingTimes || defaultOpeningTimes;
        setOpeningTimes(firstTimes);
        setOcsCode(first.ocsCode || "");
        setMemberEmail(first.email || "");

        // Load patient app settings from first pharmacy
        const patientSettings = first.patientAppSettings || { ...defaultPatientAppSettings };
        setPatientAppSettings(patientSettings);
      } else {
        setOpeningTimes(defaultOpeningTimes);
        setOcsCode("");
        setMemberEmail("");
        setPatientAppSettings({ ...defaultPatientAppSettings });
      }

      setSelectedPharmacyIndex(0);
      setTabValue(0);
    }
  }, [open]);

  const handleCheckboxChange = (field) => {
    const updated = { ...checkboxes, [field]: !checkboxes[field] };
    setCheckboxes(updated);
    setHasChanges(true);
  };

  const handleSelectPharmacy = (idx) => {
    setSelectedPharmacyIndex(idx);
    const sel = (editedData?.members || [])[idx];
    setOpeningTimes(sel?.openingTimes || defaultOpeningTimes);
    setOcsCode(sel?.ocsCode || "");
    setMemberEmail(sel?.email || "");
    setPatientAppSettings(sel?.patientAppSettings || { ...defaultPatientAppSettings });
  };

  const updateOpeningTime = (day, field, value) => {
    const next = { ...openingTimes, [day]: { ...openingTimes[day], [field]: value } };
    setOpeningTimes(next);
    setHasChanges(true);
  };

  const updatePatientAppSetting = (key, value) => {
    const updated = { ...patientAppSettings, [key]: value };
    setPatientAppSettings(updated);
    setHasChanges(true);
  };

  const inheritOrganisationSettings = () => {
    // TODO: Fetch organisation-level patient app settings and apply to pharmacy
    // For now, this is a placeholder
    alert("Inherit from organisation - API integration pending");
  };

  const addPharmacy = () => {
    const name = (newPharmacyName || "").trim();
    const code = (newPharmacyCode || "").trim();
    if (!name) { setAddError("Pharmacy name is required"); return; }

    const nextMembers = Array.isArray(editedData?.members) ? [...editedData.members] : [];
    nextMembers.push({
      pharmacyName: name,
      pharmacyId: code || name,
      openingTimes: defaultOpeningTimes,
      ocsCode: "",
      email: "",
      patientAppSettings: { ...defaultPatientAppSettings }
    });

    const nextEdited = { ...editedData, members: nextMembers };
    setEditedData(nextEdited);
    setNewPharmacyName("");
    setNewPharmacyCode("");
    setAddError("");
    setAddDialogOpen(false);
    setSelectedPharmacyIndex(nextMembers.length - 1);
    setOpeningTimes(defaultOpeningTimes);
    setMemberEmail("");
    setPatientAppSettings({ ...defaultPatientAppSettings });
    setHasChanges(true);
  };

  const deleteSelectedPharmacy = () => {
    const list = Array.isArray(editedData?.members) ? [...editedData.members] : [];
    if (!list.length) return;
    if (!window.confirm("Remove this pharmacy from the group?")) return;

    list.splice(selectedPharmacyIndex, 1);
    const nextEdited = { ...editedData, members: list };
    setEditedData(nextEdited);

    const nextIndex = Math.max(0, selectedPharmacyIndex - 1);
    setSelectedPharmacyIndex(nextIndex);
    const sel = list[nextIndex];
    setOpeningTimes(sel?.openingTimes || defaultOpeningTimes);
    setOcsCode(sel?.ocsCode || "");
    setMemberEmail(sel?.email || "");
    setPatientAppSettings(sel?.patientAppSettings || { ...defaultPatientAppSettings });
    setHasChanges(true);
  };

  const handleSave = async () => {
    const updated = { ...editedData };
    if (Array.isArray(updated.members) && updated.members.length > 0) {
      updated.members[selectedPharmacyIndex] = {
        ...(updated.members[selectedPharmacyIndex] || {}),
        openingTimes: openingTimes,
        ocsCode: ocsCode,
        email: memberEmail,
        patientAppSettings: patientAppSettings
      };
    }

    const dataToSave = {
      ...updated,
      ...checkboxes,
      timesheet_day: timesheetDay,
      _id: localStorage.getItem("userId"),
      email: groupData?.email || "",
      manager_email: managerEmail,
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.pharmacegroup.co.uk/updateGroupSettings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        console.error("Failed to update organisation settings");
        return;
      }

      localStorage.setItem("manager_email", managerEmail);
      localStorage.setItem("organisationName", dataToSave.organisation_name);
      localStorage.setItem("expireBefore", dataToSave.expireBefore);
      localStorage.setItem("slowBefore", dataToSave.slowBefore);
      localStorage.setItem("members", JSON.stringify(dataToSave.members));

      Object.entries(checkboxes).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });

      localStorage.setItem("timesheet_day", timesheetDay);

      setGroupData(editedData);
      setHasChanges(false);
      onClose();
    } catch (err) {
      console.error("Error saving settings:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Configuration</DialogTitle>
      <DialogContent>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Organisation Settings" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
          <Tab label="Site Settings" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* ORGANISATION SETTINGS TAB */}
          <div style={{ maxWidth: 400 }}>
            <h4>Manager reports</h4>

            <InputLabel>Manager Email</InputLabel>
            <Input
              type="email"
              fullWidth
              value={managerEmail}
              onChange={(e) => {
                setManagerEmail(e.target.value);
                setHasChanges(true);
              }}
              style={{ marginBottom: 12 }}
            />

            <div style={{ marginTop: "1rem" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_sim}
                    onChange={() => handleCheckboxChange("report_sim")}
                  />
                }
                label="SIM activity"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_expiring}
                    onChange={() => handleCheckboxChange("report_expiring")}
                  />
                }
                label="Expiring items"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_slow}
                    onChange={() => handleCheckboxChange("report_slow")}
                  />
                }
                label="Slow movers"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_fridge}
                    onChange={() => handleCheckboxChange("report_fridge")}
                  />
                }
                label="Fridge checks"
              />

              <div style={{ display: "flex", alignItems: "center", marginTop: 8, gap: 8 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.send_timesheets}
                      onChange={() => handleCheckboxChange("send_timesheets")}
                    />
                  }
                  label="Send monthly timesheets"
                />
                <TextField
                  type="number"
                  label="Timesheet day"
                  size="small"
                  value={timesheetDay}
                  onChange={(e) => {
                    const val = Math.min(31, Math.max(1, parseInt(e.target.value) || 1));
                    setTimesheetDay(val);
                    setHasChanges(true);
                  }}
                  inputProps={{ min: 1, max: 31 }}
                  style={{ width: 120 }}
                />
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* SITE SETTINGS TAB */}
          <div style={{ display: "flex", flexDirection: "row", gap: 24 }}>
            {/* LEFT: Pharmacies list */}
            <div style={{ flex: 0.8, minWidth: 200 }}>
              <h4>Pharmacies</h4>

              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <Button variant="outlined" size="small" onClick={() => setAddDialogOpen(true)}>
                  Add pharmacy
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={deleteSelectedPharmacy}
                  disabled={!editedData?.members?.length}
                >
                  Remove selected
                </Button>
              </div>

              <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, maxHeight: 300, overflow: "auto" }}>
                {(editedData?.members?.length ? editedData.members : [])
                  .map((m, idx) => ({ m, idx }))
                  .sort((a, b) =>
                    (a.m.pharmacyName || a.m.pharmacyId || "").localeCompare(
                      b.m.pharmacyName || b.m.pharmacyId || "", undefined, { sensitivity: "base" }
                    )
                  )
                  .map(({ m, idx }) => (
                    <div
                      key={`${m.pharmacyId || m.pharmacyName || idx}-${idx}`}
                      onClick={() => handleSelectPharmacy(idx)}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                        background: idx === selectedPharmacyIndex ? "#f0f9ff" : "transparent",
                        borderBottom: "1px solid #f3f4f6"
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{m.pharmacyName || m.pharmacyId}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{m.pharmacyId}</div>
                    </div>
                  ))}
                {(!editedData?.members || editedData.members.length === 0) && (
                  <div style={{ padding: 10, color: "#6b7280" }}>No pharmacies yet.</div>
                )}
              </div>
            </div>

            {/* MIDDLE/RIGHT: Details for selected pharmacy */}
            <div style={{ flex: 2 }}>
              {/* Opening times */}
              <div style={{ marginBottom: 24 }}>
                <h4>Opening times</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {Object.keys(openingTimes).map((day) => (
                    <div
                      key={day}
                      style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8 }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>{day}</div>
                      <TextField
                        label="Open"
                        size="small"
                        value={openingTimes[day].open}
                        onChange={(e) => updateOpeningTime(day, "open", e.target.value)}
                      />
                      <TextField
                        label="Close"
                        size="small"
                        value={openingTimes[day].close}
                        onChange={(e) => updateOpeningTime(day, "close", e.target.value)}
                        style={{ marginLeft: 8 }}
                      />
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 16 }}>
                  <InputLabel>OCS Code</InputLabel>
                  <Input
                    value={ocsCode}
                    onChange={(e) => {
                      setOcsCode(e.target.value);
                      setHasChanges(true);
                    }}
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: 16 }}>
                  <InputLabel>Contact email (for Action Centre messages)</InputLabel>
                  <Input
                    type="email"
                    value={memberEmail}
                    onChange={(e) => {
                      setMemberEmail(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="branch@example.com"
                    fullWidth
                  />
                </div>
              </div>

              {/* Patient App Settings */}
              <PatientAppSection
                settings={patientAppSettings}
                onUpdate={updatePatientAppSetting}
                onInherit={inheritOrganisationSettings}
              />
            </div>
          </div>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={!hasChanges} variant="contained">
          Save changes
        </Button>
      </DialogActions>

      {/* Add pharmacy dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add pharmacy</DialogTitle>
        <DialogContent>
          <InputLabel>Name</InputLabel>
          <Input fullWidth value={newPharmacyName} onChange={(e) => setNewPharmacyName(e.target.value)} />
          <InputLabel sx={{ mt: 2 }}>Code / ID (optional)</InputLabel>
          <Input fullWidth value={newPharmacyCode} onChange={(e) => setNewPharmacyCode(e.target.value)} />
          {addError && <div style={{ color: "crimson", marginTop: 8 }}>{addError}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={addPharmacy} disabled={!newPharmacyName.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default Settings;